import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Check, ChevronsUpDown } from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ResponsiveDialogButton } from "@/components/shared/responsive-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getCsrfToken } from "@/lib/csrf";
import { groupLocationsByCountry } from "@/lib/locations";
import { cn } from "@/lib/utils";
import type { Location } from "@/types/admin-portal/location";
import type { MitraBlastConfig } from "@/types/admin-portal/mitra-blast-config";
import type { ResponsiveDialogMode } from "@/types/globals";

interface LocationServiceEntry {
	locationId: number;
	serviceId: number;
	serviceName: string;
}

interface MitraBlastConfigFormProps {
	locations: Location[];
	locationServices: LocationServiceEntry[];
	initialData?: MitraBlastConfig;
	onSuccess?: () => void;
	forceMode?: ResponsiveDialogMode;
}

export function MitraBlastConfigForm({
	locations,
	locationServices,
	initialData,
	onSuccess,
	forceMode,
}: MitraBlastConfigFormProps) {
	const { t } = useTranslation("mitra-blast-configs");
	const [isLoading, setIsLoading] = useState(false);
	const [serverError, setServerError] = useState<string | null>(null);
	const [openLocation, setOpenLocation] = useState(false);
	const [openService, setOpenService] = useState(false);

	const isEdit = useMemo(() => !!initialData, [initialData]);

	const formSchema = z.object({
		locationId: z.coerce.number().min(1, t("fields.location.placeholder")),
		serviceId: z.coerce.number().min(1, t("fields.service.placeholder")),
		telegramGroupId: z.string().optional(),
		whatsappGroupId: z.string().optional(),
		isActive: z.boolean().default(true),
		notes: z.string().optional(),
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			locationId: initialData?.locationId || 0,
			serviceId: initialData?.serviceId || 0,
			telegramGroupId: initialData?.telegramGroupId || "",
			whatsappGroupId: initialData?.whatsappGroupId || "",
			isActive: initialData?.isActive ?? false,
			notes: initialData?.notes || "",
		},
	});

	const groupedLocations = useMemo(
		() => groupLocationsByCountry(locations),
		[locations],
	);

	const selectedLocationId = form.watch("locationId");

	const filteredServices = useMemo(
		() =>
			selectedLocationId
				? locationServices.filter((ls) => ls.locationId === selectedLocationId)
				: [],
		[locationServices, selectedLocationId],
	);

	const onSubmit = useCallback(
		async (values: z.infer<typeof formSchema>) => {
			setIsLoading(true);
			setServerError(null);
			try {
				const url = isEdit
					? `/admin-portal/mitra-blast-configs/${initialData?.id}`
					: "/admin-portal/mitra-blast-configs";
				const method = isEdit ? "PATCH" : "POST";

				const response = await fetch(url, {
					method: method,
					headers: {
						"Content-Type": "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({
						mitra_blast_config: {
							location_id: values.locationId,
							service_id: values.serviceId,
							telegram_group_id: values.telegramGroupId,
							whatsapp_group_id: values.whatsappGroupId,
							is_active: values.isActive,
							notes: values.notes,
						},
					}),
				});

				const data = await response.json();

				if (data.success) {
					toast.success(
						data.message ||
							(isEdit ? t("toast.update_success") : t("toast.create_success")),
					);
					onSuccess?.();
				} else {
					const errorMessage =
						data.errors?.join(", ") ||
						data.message ||
						(isEdit ? t("toast.update_error") : t("toast.create_error"));
					setServerError(errorMessage);
					throw new Error(errorMessage);
				}
			} catch (error) {
				console.error("Failed to submit blast config:", error);
				toast.error(
					error instanceof Error
						? error.message
						: isEdit
							? t("toast.update_error")
							: t("toast.create_error"),
				);
			} finally {
				setIsLoading(false);
			}
		},
		[isEdit, initialData?.id, onSuccess, t],
	);

	const buttonProps = useMemo(
		() => ({
			isLoading,
			forceMode,
			submitText: isEdit ? t("button.update_data") : t("button.add_data"),
		}),
		[isLoading, forceMode, t, isEdit],
	);

	return (
		<div className="p-0.5 min-h-fit">
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
					<FormField
						control={form.control}
						name="locationId"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								<FormLabel>{t("fields.location.label")}</FormLabel>
								<Popover
									open={openLocation}
									onOpenChange={setOpenLocation}
									modal
								>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												variant="outline"
												className={cn(
													"w-full justify-between shadow-inner bg-input",
													!field.value && "font-normal text-muted-foreground",
												)}
												disabled={isLoading || isEdit}
											>
												{field.value
													? locations.find(
															(location) => location.id === field.value,
														)?.city || initialData?.locationCity
													: t("fields.location.placeholder")}
												<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
										<Command>
											<CommandInput
												placeholder={t("fields.location.placeholder")}
												className="h-9"
												autoComplete="address-level2"
											/>
											<CommandList>
												<CommandEmpty>
													{t("fields.location.empty")}
												</CommandEmpty>
												{groupedLocations.map((group) => (
													<Fragment key={group.country}>
														<span className="block bg-primary px-2 py-2 text-xs font-bold text-primary-foreground">
															{group.country}
														</span>
														{group.states.map((state) => (
															<CommandGroup
																key={state.name}
																heading={state.name}
															>
																{state.cities.map((city) => (
																	<CommandItem
																		value={city.name}
																		key={city.id}
																		onSelect={() => {
																			form.setValue("locationId", city.id);
																			setOpenLocation(false);
																		}}
																		className="justify-between"
																	>
																		<span>{city.name}</span>
																		<Check
																			className={cn(
																				"ml-auto h-4 w-4",
																				city.id === field.value
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																	</CommandItem>
																))}
															</CommandGroup>
														))}
													</Fragment>
												))}
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="serviceId"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								<FormLabel>{t("fields.service.label")}</FormLabel>
								<Popover
									open={openService}
									onOpenChange={(open) => {
										if (selectedLocationId) setOpenService(open);
									}}
									modal
								>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												variant="outline"
												className={cn(
													"w-full justify-between shadow-inner bg-input",
													!field.value && "font-normal text-muted-foreground",
												)}
												disabled={isLoading || !selectedLocationId}
											>
												{field.value && filteredServices.length > 0
													? filteredServices
															.find((s) => s.serviceId === field.value)
															?.serviceName.replaceAll("_", " ")
													: !selectedLocationId
														? t("fields.service.no_location")
														: t("fields.service.placeholder")}
												<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
										<Command>
											<CommandInput
												placeholder={t("fields.service.placeholder")}
												className="h-9"
											/>
											<CommandList>
												<CommandEmpty>{t("fields.service.empty")}</CommandEmpty>
												<CommandGroup>
													{filteredServices.map((s) => (
														<CommandItem
															key={s.serviceId}
															value={s.serviceName}
															onSelect={() => {
																form.setValue("serviceId", s.serviceId);
																setOpenService(false);
															}}
															className="justify-between"
														>
															<span>{s.serviceName.replaceAll("_", " ")}</span>
															<Check
																className={cn(
																	"ml-auto h-4 w-4",
																	s.serviceId === field.value
																		? "opacity-100"
																		: "opacity-0",
																)}
															/>
														</CommandItem>
													))}
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="telegramGroupId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("fields.telegram_group_id.label")}{" "}
									<span className="text-sm italic font-light ">
										&mdash; ({t("fields.optional")})
									</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder={t("fields.telegram_group_id.placeholder")}
										className="shadow-inner bg-input"
										disabled={isLoading}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="whatsappGroupId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("fields.whatsapp_group_id.label")}{" "}
									<span className="text-sm italic font-light ">
										&mdash; ({t("fields.optional")})
									</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder={t("fields.whatsapp_group_id.placeholder")}
										className="shadow-inner bg-input"
										disabled={isLoading}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="isActive"
						render={({ field }) => (
							<FormItem className="relative flex w-full items-start space-y-0 gap-3 rounded-md bg-input  border border-input p-4 shadow-xs outline-none has-data-[state=checked]:border-primary/50">
								<FormControl>
									<Switch
										checked={field.value}
										onCheckedChange={field.onChange}
										disabled={isLoading}
										className="data-[state=checked]:[&_span]:rtl:-translate-x-4 order-1 h-4 w-8 after:absolute after:inset-0 [&_span]:size-4 border border-input data-[state=checked]:[&_span]:translate-x-4 shadow"
									/>
								</FormControl>
								<div className="grid grow gap-2">
									<FormLabel className="uppercase">
										{t("fields.is_active.label")}
									</FormLabel>
									<p className="text-muted-foreground text-xs">
										{t("fields.is_active.description")}
									</p>
								</div>
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="notes"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("fields.notes.label")}{" "}
									<span className="text-sm italic font-light ">
										&mdash; (optional)
									</span>
								</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										placeholder={t("fields.notes.placeholder")}
										className="shadow-inner bg-input"
										disabled={isLoading}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{serverError && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertTitle>{t("common.error") || "Error"}</AlertTitle>
							<AlertDescription>{serverError}</AlertDescription>
						</Alert>
					)}

					<ResponsiveDialogButton {...buttonProps} />
				</form>
			</Form>
		</div>
	);
}
